import { maxPageQueryKeys, pageQueryKeyMaxLength, announcementBadgeMaxLength, announcementBodyMaxLength, announcementColorPattern, announcementHighlightMaxLength, announcementHighlightsMaxCount, announcementKinds, announcementLayouts, announcementModalSizes, announcementTitleMaxLength, defaultAnnouncementDelayMs, defaultNudgeDelayMs, maxAnnouncementDelayMs, maxAnnouncements, maxNudgeDelayMs, maxNudges, maxPageMessages, minAnnouncementDelayMs, minNudgeDelayMs, nudgeMaxTextLength } from './constants.js';
import { normalizePageKey } from './page.js';
import { resolveUrlRules } from './url-rules.js';

/**
 * The proactive bubbles from the config payload, validated and capped. Anything unexpected is dropped
 * rather than rendered: the launcher must survive a payload this loader does not understand.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {Array<{id: string, text: string}>}
 */
function resolveNudges(config) {
    if (!config || !Array.isArray(config.nudges)) {
        return [];
    }

    const result = [];
    for (const item of config.nudges) {
        if (!item || typeof item.id !== 'string' || typeof item.text !== 'string') {
            continue;
        }

        const text = item.text.trim();
        if (!item.id || !text || text.length > nudgeMaxTextLength) {
            continue;
        }

        result.push({ id: item.id, text: text });
        if (result.length === maxNudges) {
            break;
        }
    }

    return result;
}

/**
 * The page-specific messages from the config payload, validated and capped like the nudges. Page keys
 * are normalized once here so every later match is a plain string comparison.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {Array<{id: string, path: string, text: string}>}
 */
function resolvePageMessages(config) {
    if (!config || !Array.isArray(config.pageMessages)) {
        return [];
    }

    const result = [];
    for (const item of config.pageMessages) {
        if (!item || typeof item.id !== 'string' || typeof item.path !== 'string'
            || typeof item.text !== 'string') {
            continue;
        }

        const text = item.text.trim();
        if (!item.id || !text || text.length > nudgeMaxTextLength) {
            continue;
        }

        // The answer is optional in the payload only so an older cached config still renders its
        // questions; the server always sends one, falling back to its own localized default.
        const reply = typeof item.reply === 'string' ? item.reply.trim() : '';

        result.push({
            id: item.id,
            // Empty for a payload that predates the field, which then matches on any host — the same
            // behaviour this loader had before pages became per-domain, rather than a silent blackout.
            host: typeof item.host === 'string' ? item.host.toLowerCase() : '',
            // The key, not the bare path: on a site whose screens live in a query parameter the server
            // files each screen's question under its own key, and cutting the query off here would hand
            // every one of them back to the first question written behind that path.
            path: normalizePageKey(item.path),
            text: text,
            reply: reply.length > 0 && reply.length <= nudgeMaxTextLength ? reply : '',
            // Who the question is for: 'provider' for the platform widget's second question per page,
            // '' for everyone else — and for every payload that predates the field.
            audience: item.audience === 'provider' ? 'provider' : '',
            // Only a key the server folded (our own dashboard) reads "*" as "any one segment"; see
            // pageKeyMatches. On every other entry it is a character like any other.
            folded: item.folded === true
        });
        if (result.length === maxPageMessages) {
            break;
        }
    }

    return result;
}

/**
 * The campaigns from the config payload, validated and capped like everything else here: an entry this
 * loader does not understand is dropped, never rendered. Kind, display and frequency are normalized to
 * their closed vocabularies with the quiet value as the fallback (a card, once per visitor), and the
 * paths are normalized once so every later match is a plain string comparison.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {Array<{id: string, kind: string, display: string, layout: string, freq: string,
 *                  size: string, delayMs: number, paths: Array<string>, title: string, body: string,
 *                  badge: string, highlights: Array<string>, imageUrl: string|null, accent: string|null,
 *                  accentDeep: string|null, ctaKind: string, ctaLabel: string,
 *                  ctaScreen: string|null, ctaUrl: string|null}>}
 */
export function resolveAnnouncements(config) {
    if (!config || !Array.isArray(config.announcements)) {
        return [];
    }

    const result = [];
    for (const item of config.announcements) {
        if (!item || typeof item.id !== 'string' || !item.id || typeof item.title !== 'string') {
            continue;
        }

        const title = item.title.trim();
        if (!title || title.length > announcementTitleMaxLength) {
            continue;
        }

        const body = typeof item.body === 'string' ? item.body.trim() : '';
        const display = item.display === 'modal' ? 'modal' : 'card';
        let layout = announcementLayouts.includes(item.layout) ? item.layout : 'classic';
        if (layout === 'spotlight' && display !== 'modal') {
            layout = 'classic'; // The split stage needs the modal's room; a card falls back quietly.
        }

        result.push({
            id: item.id,
            kind: announcementKinds.includes(item.kind) ? item.kind : 'news',
            display: display,
            layout: layout,
            freq: item.freq === 'session' ? 'session' : 'visitor',
            size: announcementModalSizes.includes(item.size) ? item.size : 'standard',
            delayMs: resolveAnnouncementDelay(item.delayMs),
            urlRules: resolveUrlRules(item, 'urlRules'),
            title: title,
            body: body.slice(0, announcementBodyMaxLength),
            accent: resolveAnnouncementColor(item.accent),
            accentDeep: resolveAnnouncementColor(item.accentDeep),
            ...resolveAnnouncementSpotlight(item),
            ...resolveAnnouncementCta(item)
        });
        if (result.length === maxAnnouncements) {
            break;
        }
    }

    return result;
}

/**
 * One campaign's waiting time in milliseconds. Anything unusable — a missing field on an older payload,
 * a number outside the supported range — becomes the campaigns' default rather than nothing: a
 * campaign without a readable timing is still a campaign the contractor wants shown.
 * @param {*} value - The raw delayMs field of one campaign entry
 * @returns {number}
 */
function resolveAnnouncementDelay(value) {
    const delay = Number(value);

    return isFinite(delay) && delay >= minAnnouncementDelayMs && delay <= maxAnnouncementDelayMs
        ? delay
        : defaultAnnouncementDelayMs;
}

/**
 * One campaign colour, or null when it is unusable. Null is not a failure here: the card then simply
 * keeps the hue its kind's rule in leadwin-nudge.css paints, which is what an older server sends anyway.
 * @param {*} value - The raw accent/accentDeep field of one campaign entry
 * @returns {string|null}
 */
function resolveAnnouncementColor(value) {
    return typeof value === 'string' && announcementColorPattern.test(value.trim())
        ? value.trim()
        : null;
}

/**
 * The spotlight half of one campaign entry, validated and capped on its own: badge and chips like every
 * other contractor text here, the image like the trust pill's href — a picture an embed loads on
 * somebody else's website is only ever an absolute https URL, anything else renders single-column.
 * @param {object} item - One raw entry of the announcements payload array
 * @returns {{badge: string, highlights: Array<string>, imageUrl: string|null}}
 */
function resolveAnnouncementSpotlight(item) {
    const highlights = Array.isArray(item.highlights)
        ? item.highlights
            .filter(function (entry) { return typeof entry === 'string' && entry.trim(); })
            .slice(0, announcementHighlightsMaxCount)
            .map(function (entry) { return entry.trim().slice(0, announcementHighlightMaxLength); })
        : [];

    return {
        badge: typeof item.badge === 'string'
            ? item.badge.trim().slice(0, announcementBadgeMaxLength)
            : '',
        highlights: highlights,
        imageUrl: typeof item.imageUrl === 'string' && item.imageUrl.indexOf('https://') === 0
            && item.imageUrl.length <= 2000
            ? item.imageUrl
            : null
    };
}

/**
 * The visitor-language kind labels the minimal campaign layout wears as its small eyebrow, or null
 * when the server sent none. Each word is validated on its own; a missing one drops that eyebrow,
 * never the campaign.
 * @param {object|null} config - Parsed config JSON or null on failure
 * @returns {{news: string, teaser: string, offer: string}|null}
 */
export function resolveAnnouncementKindLabels(config) {
    const raw = config ? config.announcementKindLabels : null;
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const labels = {};
    for (const kind of announcementKinds) {
        labels[kind] = typeof raw[kind] === 'string' ? raw[kind].trim().slice(0, 40) : '';
    }

    return labels;
}

/**
 * The CTA half of one campaign entry, or the explicit "no CTA" when anything about it is unusable: a
 * campaign with a broken button still has something to say, so the button is what gets dropped. A link
 * CTA exists only with an absolute https target — the same rule the trust pill's href lives by.
 * @param {object} item - One raw entry of the announcements payload array
 * @returns {{ctaKind: string, ctaLabel: string, ctaScreen: string|null, ctaUrl: string|null}}
 */
function resolveAnnouncementCta(item) {
    const none = { ctaKind: 'none', ctaLabel: '', ctaScreen: null, ctaUrl: null };
    const label = typeof item.ctaLabel === 'string' ? item.ctaLabel.trim().slice(0, 80) : '';
    if (!label) {
        return none; // A button without words is not a button.
    }

    switch (item.ctaKind) {
        case 'openchat':
        case 'askquestion':
            return { ctaKind: item.ctaKind, ctaLabel: label, ctaScreen: null, ctaUrl: null };
        case 'openscreen':
            return item.ctaScreen === 'lead' || item.ctaScreen === 'booking'
                ? { ctaKind: 'openscreen', ctaLabel: label, ctaScreen: item.ctaScreen, ctaUrl: null }
                : none;
        case 'link':
            return typeof item.ctaUrl === 'string' && item.ctaUrl.indexOf('https://') === 0
                && item.ctaUrl.length <= 2000
                ? { ctaKind: 'link', ctaLabel: label, ctaScreen: null, ctaUrl: item.ctaUrl }
                : none;
        default:
            return none; // A newer CTA vocabulary against this loader stays a plain card.
    }
}

/**
 * The query parameters that name a page on this site, validated and capped like everything else here.
 * Deliberately NOT part of resolveNudgeOptions below: that whole object is replaced from the live save
 * broadcast, which carries the bubble settings and nothing else — reading this there would wipe the page
 * identity the moment a provider saved a bubble, and every screen would collapse back onto its path.
 * @param {object|null} config - Parsed config JSON
 * @returns {Array<string>} Lowercased parameter names; empty means the path alone is the page
 */
export function resolvePageQueryKeys(config) {
    const raw = config ? config.pageQueryKeys : null;

    if (!Array.isArray(raw)) {
        return []; // Missing on an older payload: the path is the page, which is today's behaviour.
    }

    const result = [];

    for (const item of raw) {
        const key = typeof item === 'string' ? item.trim().toLowerCase() : '';

        if (key.length > 0 && key.length <= pageQueryKeyMaxLength && result.indexOf(key) < 0) {
            result.push(key);
        }

        if (result.length === maxPageQueryKeys) {
            break;
        }
    }

    return result;
}

/**
 * Normalizes the bubble half of a payload. Split out because the live settings broadcast carries exactly
 * these fields under the same names, and a second reading of them would be a second set of defaults.
 * @param {object|null} config - Parsed config JSON, or a live settings message
 * @returns {{nudges: Array, pageMessages: Array, nudgeDelayMs: number, nudgeEveryPage: boolean,
 *            nudgeHideOnMobile: boolean, nudgeCloseLabel: string}}
 */
export function resolveNudgeOptions(config) {
    const delay = config ? Number(config.nudgeDelayMs) : NaN;

    return {
        nudges: resolveNudges(config),
        pageMessages: resolvePageMessages(config),
        // Whether this widget writes questions for the pages visitors stand on. Off by default and for
        // any payload that predates the field, because the only thing it enables is an extra request:
        // without it a widget that never covers a page would report every page load for nothing.
        pageCoverage: config ? config.pageCoverage === true : false,
        nudgeDelayMs: isFinite(delay) && delay >= minNudgeDelayMs && delay <= maxNudgeDelayMs
            ? delay
            : defaultNudgeDelayMs,
        // Anything but an explicit true stays on the quiet default, including an older payload that
        // predates either setting.
        nudgeEveryPage: config ? config.nudgeEveryPage === true : false,
        nudgeHideOnMobile: config ? config.nudgeHideOnMobile === true : false,
        // Which pages the bubbles are allowed on at all. Empty for every widget that never opened the
        // setting, and for any payload predating it — both mean "every page".
        nudgeUrlRules: resolveUrlRules(config, 'nudgeUrlRules'),
        nudgeCloseLabel: resolveLabel(config, 'nudgeCloseLabel', 'Close'),
        nudgeOptOutLabel: resolveLabel(config, 'nudgeOptOutLabel', ''),
        nudgeOptOutDoneLabel: resolveLabel(config, 'nudgeOptOutDoneLabel', ''),
        // Empty on a payload that predates the undo chip, and the corner then simply does not offer it —
        // the header menu's way back is unaffected either way.
        nudgeOptOutUndoLabel: resolveLabel(config, 'nudgeOptOutUndoLabel', '')
    };
}

/**
 * One label from the payload, capped and defaulted. The opt-out labels default to EMPTY rather than to
 * English: the close control has to be operable whatever arrives, but an offer nobody localized is one
 * the visitor should simply not be shown — an older server that does not send them yet keeps today's
 * behaviour instead of showing a stray English line on a German website.
 * @param {object|null} config - Parsed config JSON, or a live settings message
 * @param {string} name - Field to read
 * @param {string} fallback - Value to use when the field is missing or unusable
 * @returns {string}
 */
export function resolveLabel(config, name, fallback) {
    const value = config ? config[name] : null;

    return typeof value === 'string' && value ? value.slice(0, 60) : fallback;
}
